import { useAppDispatch, useAppSelector } from 'app/hooks';
import barInfoService from 'features/BarGuideInfo/barInfoService';
import { BarInfo } from 'features/BarGuideInfo/models/IBarGuideInfo';
import { DisplayControlGroup } from 'features/DisplayControlGroup/models';
import displayTimingGroupService from 'features/DisplayTimingGroup/displayTimingGroupService';
import { DisplayTimingGroupListItem } from 'features/DisplayTimingGroup/models';
import CourseService from 'features/MenuBook/components/CopyModal/services';
import serviceMenuDetails from 'features/MenuDetail/pages/service';
import { Course } from 'features/SaleType/models/Course';
import moment from 'moment';
import { forwardRef, useEffect, useState } from 'react';
import { Accordion, Col, Form, Row } from 'react-bootstrap';
import ReactDatePicker from 'react-datepicker';
import { Control, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FaCalendarAlt } from 'react-icons/fa';
import { DATE_FORMAT_YYYY_MM_DD } from 'utils';
import {
  DISPLAY_LIMIT,
  DISPLAY_TIMING_RADIO,
  DISPLAY_TIMING_TYPES,
  DisplayControlGroupType,
  DisplayTimingGroupType,
  MENU_DELETE_OPTIONS,
  MENU_DISPLAY_TIMING,
  MENU_PROPERTIES,
  SEARCH_MENU_TYPE_CONDITIONS,
} from '../constants';
import menuService from '../menuService';
import { menuListActions, selectClassificationOptions } from '../menuSlice';
import { SearchMenuFilter } from '../models/menu';

interface FormSearchValues {
  menu_classification: string;
  menu_type_conditions: string;
  menu_property: string;

  search_menu_condition: string; // 1, 2
  search_display_timing: string; // used when search_menu_condition is 1
  // search_display_control_group_timing: string; // // used when search_menu_condition is 1
  // search_display_control_group: string; // used when search_menu_condition is 2

  search_display_timing_group_cd: string; // used when search_menu_condition is 1
  search_display_control_group_cd: string; // used when search_menu_condition is 1
  search_source_display_control_group_cd: string; // used when search_menu_condition is 2

  menu_code: string;
  menu_name: string;

  search_bar_guidance: string;
  search_course: string;
  search_order_limit: string;

  deleted_data: string;

  expired_date: Date;
  menu_pos: string;

  display_limit: string;
}

export interface MenuListFilterProps {
  onFilter?: (data: SearchMenuFilter) => void;
}

function MenuListFilter({ onFilter }: MenuListFilterProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  //selector
  const classificationOptions = useAppSelector(selectClassificationOptions);

  // state
  const [listDisplayControlGroup, setDisplayControlGroup] = useState<DisplayControlGroupType[]>([]);
  const [listDisplayTimingGroup, setDisplayTimingGroup] = useState<DisplayTimingGroupType[]>([]);

  const [listGuidances, setListGuidance] = useState<BarInfo[]>([]);
  const [orderLimit, setOrderLimit] = useState<{ key: number; value: string }[]>([]);
  const [listCourses, setListCourses] = useState<Course[]>([]);

  const {
    register,
    handleSubmit,
    control,
    getValues,
    setValue,
    formState: { isSubmitting },
  } = useForm<FormSearchValues>({
    defaultValues: {
      menu_classification: '',
      menu_type_conditions: '0',
      menu_property: '0',

      search_menu_condition: '1',
      search_display_timing: '0',
      search_display_control_group_cd: '',
      search_display_timing_group_cd: '',
      search_source_display_control_group_cd: '',
      menu_code: '',
      menu_name: '',

      deleted_data: '0',
      menu_pos: '',
      expired_date: new Date(),

      search_bar_guidance: '',
      search_course: '',
      search_order_limit: '',

      display_limit: '1',
    },
  });

  // watchers
  const watchSearchMenuCondition = Number(
    useWatch({
      control,
      name: 'search_menu_condition',
      defaultValue: getValues('search_menu_condition'),
    })
  );

  const watchSearchDisplayTiming = Number(
    useWatch({
      control,
      name: 'search_display_timing',
      defaultValue: getValues('search_display_timing'),
    })
  );

  const watchExpiredDate = useWatch({
    control,
    name: 'expired_date',
    defaultValue: getValues('expired_date'),
  });

  // side effect
  useEffect(() => {
    serviceMenuDetails.getDisplayControlGroups().then((res) => {
      const data: DisplayControlGroupType[] = res.data
        .filter((item: DisplayControlGroup) => !item.delete_flg)
        .map((item: DisplayControlGroup) => ({
          value: item.id,
          key: item.name,
          display_name: item.display_name,
        }));
      setDisplayControlGroup(data);
    });
  }, []);

  useEffect(() => {
    displayTimingGroupService.getAll({ active: '0' }).then((res) => {
      const data: DisplayTimingGroupType[] = res.data.map((item: DisplayTimingGroupListItem) => ({
        value: item.id,
        key: item.group_name,
        display_name: item.group_name,
      }));
      setDisplayTimingGroup(data);
    });
  }, []);

  useEffect(() => {
    dispatch(menuListActions.fetchMenuClassification());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // load bar guidance menu

  useEffect(() => {
    barInfoService.getBarInformationList(0).then((res) => {
      if (res.data) {
        setListGuidance(res.data);
      }
    });
  }, []);

  // load courses
  useEffect(() => {
    CourseService.getCourses().then((res) => {
      if (res.data) {
        setListCourses(res.data);
      }
    });
  }, []);

  //load order limit
  useEffect(() => {
    menuService.getOrderLimit().then((res) => {
      if (res.data) {
        setOrderLimit(res.data);
      }
    });
  }, []);

  // events
  function handleSearchSubmit(data: FormSearchValues) {
    let search_display_control_group_timing = '';
    if (Number(data.search_menu_condition) === 1) {
      if (Number(data.search_display_timing) === 6) {
        search_display_control_group_timing = data.search_display_control_group_cd;
      } else if (Number(data.search_display_timing) === 7) {
        search_display_control_group_timing = data.search_display_timing_group_cd;
      }
    }

    const filterPayload: SearchMenuFilter = {
      menu_classification: data.menu_classification,
      menu_type_conditions: Number(data.menu_type_conditions),
      menu_property: Number(data.menu_property),

      search_menu_condition: Number(data.search_menu_condition),
      search_display_timing: Number(data.search_display_timing),
      search_target_display_group:
        search_display_control_group_timing === '' ? null : Number(search_display_control_group_timing),
      search_source_display_control_group:
        data.search_source_display_control_group_cd === '' ? null : Number(data.search_source_display_control_group_cd),

      search_bar_guidance: data.search_bar_guidance === '' ? null : Number(data.search_bar_guidance),
      search_course: data.search_course === '' ? null : Number(data.search_course),
      search_order_limit: data.search_order_limit === '' ? null : Number(data.search_order_limit),

      menu_name: data.menu_name,
      menu_code: data.menu_code,

      deleted_data: Number(data.deleted_data),
      expired_date: data.expired_date,
      menu_pos: data.menu_pos,
      display_limit: Number(data.display_limit),
    };

    onFilter?.(filterPayload);
  }

  return (
    <Accordion className="mb-3" defaultActiveKey={['0', '1', '2']} alwaysOpen>
      <Accordion.Item eventKey="0" className="movement-information">
        <Accordion.Header>{t('MenuList.Filter.filter_condition')} </Accordion.Header>
        <Accordion.Body>
          <Form onSubmit={handleSubmit(handleSearchSubmit)}>
            <Row>
              <Col sm={3}>
                <Row className="mb-2">
                  <Col sm={5} className="text-end">
                    <Form.Label htmlFor="cbo-menu-classification" className="col-form-label">
                      {t('MenuList.Filter.menu_classification')}
                    </Form.Label>
                  </Col>
                  <Col sm={6}>
                    <Form.Select
                      id="cbo-menu-classification"
                      className="text-center"
                      {...register('menu_classification')}
                    >
                      <option value="">{t('MenuList.Filter.no_selection')}</option>
                      {classificationOptions.map((classification) => {
                        return (
                          <option key={classification.value} value={classification.value}>
                            {classification.label}
                          </option>
                        );
                      })}
                    </Form.Select>
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col sm={5} className="text-end">
                    <Form.Label htmlFor="cbo-menu-type" className="col-form-label">
                      {t('MenuList.Filter.search_menu_type_condition')}
                    </Form.Label>
                  </Col>
                  <Col sm={6}>
                    <Form.Select id="cbo-menu-type" className="text-center" {...register('menu_type_conditions')}>
                      {SEARCH_MENU_TYPE_CONDITIONS.map((item) => {
                        return (
                          <option key={item.value} value={item.value}>
                            {item.display_name}
                          </option>
                        );
                      })}
                    </Form.Select>
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col sm={5} className="text-end">
                    <Form.Label htmlFor="cbo-menu-property" className="col-form-label">
                      {t('MenuList.Filter.properties')}
                    </Form.Label>
                  </Col>
                  <Col sm={6}>
                    <Form.Select id="cbo-menu-property" className="text-center" {...register('menu_property')}>
                      {MENU_PROPERTIES.map((item) => {
                        return (
                          <option key={item.value} value={item.value}>
                            {item.label}
                          </option>
                        );
                      })}
                    </Form.Select>
                  </Col>
                </Row>
                <div>
                  <Row className="mb-2 d-none">
                    <Col sm={5} className="text-end">
                      <Form.Label htmlFor="cbo-menu-display-timing-type" className="col-form-label">
                        {t('MenuList.Filter.display_timing')}
                      </Form.Label>
                    </Col>
                    <Col sm={6}>
                      <Form.Select
                        id="cbo-menu-display-timing-type"
                        className="text-center"
                        {...register('search_menu_condition')}
                      >
                        {DISPLAY_TIMING_TYPES.map((item) => {
                          return (
                            <option key={item.value} value={item.value}>
                              {item.label}
                            </option>
                          );
                        })}
                      </Form.Select>
                    </Col>
                  </Row>
                  <div className={watchSearchMenuCondition === 1 ? '' : 'd-none'}>
                    <Row className="mb-2">
                      <Col sm={5} className="text-end">
                        <Form.Label htmlFor="cbo-menu-display-timing" className="col-form-label">
                          {t('MenuList.Filter.control_condition')}
                        </Form.Label>
                      </Col>
                      <Col sm={6}>
                        <Form.Select
                          id="cbo-menu-display-timing"
                          className="text-center"
                          {...{ ...register('search_display_timing') }}
                        >
                          <option value={0}>{t('MenuList.Filter.no_selection')}</option>
                          {MENU_DISPLAY_TIMING.map((item) => {
                            return (
                              <option key={item.value} value={item.value}>
                                {item.label}
                              </option>
                            );
                          })}
                        </Form.Select>
                      </Col>
                    </Row>
                    <div className={watchSearchDisplayTiming === DISPLAY_TIMING_RADIO.CONTROL_GROUP ? '' : 'd-none'}>
                      <Row className="mb-2">
                        <Col sm={5} className="text-end">
                          <Form.Label htmlFor="cbo-display-control-group1" className="col-form-label">
                            {t('MenuList.Filter.display_control_group')}
                          </Form.Label>
                        </Col>
                        <Col sm={6}>
                          <Form.Select
                            id="cbo-display-control-group1"
                            defaultValue={0}
                            className="text-center"
                            {...register('search_display_control_group_cd')}
                          >
                            <option value={0}>{t('MenuList.Filter.no_selection')}</option>
                            {listDisplayControlGroup.map((item) => {
                              return (
                                <option key={item.value} value={item.value}>
                                  {item.key}
                                </option>
                              );
                            })}
                          </Form.Select>
                        </Col>
                      </Row>
                    </div>
                    <div className={watchSearchDisplayTiming === DISPLAY_TIMING_RADIO.TIMING_GROUP ? '' : 'd-none'}>
                      <Row className="mb-2">
                        <Col sm={5} className="text-end">
                          <Form.Label htmlFor="cbo-display-timing-group" className="col-form-label">
                            {t('MenuList.Filter.display_timing_group')}
                          </Form.Label>
                        </Col>
                        <Col sm={6}>
                          <Form.Select
                            id="cbo-display-timing-group"
                            defaultValue={0}
                            className="text-center"
                            {...register('search_display_timing_group_cd')}
                          >
                            <option value={0}>{t('MenuList.Filter.no_selection')}</option>
                            {listDisplayTimingGroup.map((item) => {
                              return (
                                <option key={item.value} value={item.value}>
                                  {item.key}
                                </option>
                              );
                            })}
                          </Form.Select>
                        </Col>
                      </Row>
                    </div>
                  </div>
                  <div className={watchSearchMenuCondition === 2 ? '' : 'd-none'}>
                    <Row className="mb-2">
                      <Col sm={5} className="text-end">
                        <Form.Label htmlFor="cbo-display-control-group2" className="col-form-label">
                          {t('MenuList.Filter.display_control_group')}
                        </Form.Label>
                      </Col>
                      <Col sm={6}>
                        <Form.Select
                          id="cbo-display-control-group2"
                          defaultValue={0}
                          className="text-center"
                          {...register('search_source_display_control_group_cd')}
                        >
                          <option value={0}>{t('MenuList.Filter.no_selection')}</option>
                          {listDisplayControlGroup.map((item) => {
                            return (
                              <option key={item.value} value={item.value}>
                                {item.key}
                              </option>
                            );
                          })}
                        </Form.Select>
                      </Col>
                    </Row>
                  </div>
                </div>
              </Col>
              <Col sm={3} style={{ borderRight: '1px solid #ddd' }}>
                <Row className="mb-2">
                  <Col sm={5} className="text-end">
                    <Form.Label htmlFor="cbo-status-data" className="col-form-label">
                      {t('MenuList.Filter.deleted_data')}
                    </Form.Label>
                  </Col>
                  <Col sm={6}>
                    <Form.Select id="cbo-status-data" className="text-center" {...register('deleted_data')}>
                      {MENU_DELETE_OPTIONS.map((item) => {
                        return (
                          <option key={item.value} value={item.value}>
                            {item.key}
                          </option>
                        );
                      })}
                    </Form.Select>
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col sm={5} className="text-end">
                    <Form.Label htmlFor="txt-menu-name" className="col-form-label">
                      {t('MenuList.Filter.menu_name')}
                    </Form.Label>
                  </Col>
                  <Col sm={6}>
                    <Form.Control type="text" id="txt-menu-name" {...register('menu_name')} />
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col sm={5} className="text-end">
                    <Form.Label htmlFor="txt-menu-code" className="col-form-label">
                      {t('MenuList.Filter.menu_code')}
                    </Form.Label>
                  </Col>
                  <Col sm={6}>
                    <Form.Control type="text" id="txt-menu-code" {...register('menu_code')} />
                  </Col>
                </Row>
              </Col>
              <Col sm={3} style={{ borderRight: '1px solid #ddd' }}>
                <Row className="mb-2">
                  <Col sm={5} className="text-end">
                    <div className="col-form-label">{t('MenuList.Filter.bar_guidance_menu')}</div>
                  </Col>
                  <Col sm={7}></Col>
                </Row>
                <Row className="mb-2">
                  <Col sm={5} className="text-end">
                    <Form.Label htmlFor="cbo-guidance-name" className="col-form-label">
                      {t('MenuList.Filter.guidance_name')}
                    </Form.Label>
                  </Col>
                  <Col sm={5}>
                    <Form.Select id="cbo-guidance-name" className="text-center" {...register('search_bar_guidance')}>
                      <option value="">{t('MenuList.Filter.no_selection')}</option>
                      {listGuidances.map((item) => {
                        return (
                          <option key={item.id} value={item.id}>
                            {item.name}
                          </option>
                        );
                      })}
                    </Form.Select>
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col sm={5} className="text-end">
                    <div className="col-form-label">{t('MenuList.Filter.course_target_menu')}</div>
                  </Col>
                  <Col sm={7}></Col>
                </Row>
                <Row className="mb-2">
                  <Col sm={5} className="text-end">
                    <Form.Label htmlFor="cbo-courses" className="col-form-label">
                      コース名：
                    </Form.Label>
                  </Col>
                  <Col sm={5}>
                    <Form.Select id="cbo-courses" className="text-center" {...register('search_course')}>
                      <option value="">{t('MenuList.Filter.no_selection')}</option>
                      {listCourses.map((course, index) => {
                        return (
                          <option key={index} value={course.id}>
                            {course.name}
                          </option>
                        );
                      })}
                    </Form.Select>
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col sm={5} className="text-end ps-0">
                    <div className="col-form-label">{t('MenuList.Filter.course_set_condition_menu')}</div>
                  </Col>
                  <Col sm={7}></Col>
                </Row>
                <Row className="mb-2">
                  <Col sm={5} className="text-end">
                    <Form.Label htmlFor="cbo-course-limits" className="col-form-label">
                      コース名：
                    </Form.Label>
                  </Col>
                  <Col sm={5}>
                    <Form.Select id="cbo-course-limits" className="text-center" {...register('search_order_limit')}>
                      <option value="">{t('MenuList.Filter.no_selection')}</option>
                      {orderLimit.map((order, index) => {
                        return (
                          <option key={order.key} value={order.key}>
                            {order.value}
                          </option>
                        );
                      })}
                    </Form.Select>
                  </Col>
                </Row>
              </Col>
              <Col sm={3}>
                <div className="d-flex h-100 flex-column justify-content-between">
                  <div>
                    <div className="mb-2">
                      <fieldset className="border px-3">
                        <legend className="float-none small w-auto px-3">
                          {t('MenuList.Filter.related_cash_register_information')}
                        </legend>
                        <div className="ps-3 mb-2">
                          {t('MenuList.Filter.extract_the_menu_from_the_related_cash_register_information')}
                        </div>
                        <div style={{ marginLeft: '-1rem', marginRight: '-1rem' }}>
                          <Row className="mb-2">
                            <Col sm={5} className="text-end">
                              <Form.Label htmlFor="cbo-status-data" className="col-form-label">
                                {t('MenuList.Filter.date_of_expires')}：
                              </Form.Label>
                            </Col>
                            <Col sm={5}>
                              <div style={{ maxWidth: '100%' }}>
                                <ReactDatePicker
                                  locale="ja"
                                  selected={watchExpiredDate}
                                  onChange={(date: Date) => {
                                    setValue('expired_date', date);
                                  }}
                                  customInput={<CustomInputDatePicker readonly />}
                                  dateFormat="yyyy/MM/dd"
                                />
                              </div>
                            </Col>
                          </Row>
                          <Row className="mb-3">
                            <Col sm={5} className="text-end">
                              <Form.Label htmlFor="txt-pos" className="col-form-label">
                                {t('MenuList.Filter.pos')}：
                              </Form.Label>
                            </Col>
                            <Col sm={5}>
                              <Form.Control type="text" id="txt-pos" {...register('menu_pos')} />
                            </Col>
                          </Row>
                        </div>
                      </fieldset>
                    </div>

                    <Row className="mb-2">
                      <Col sm={5} className="text-end">
                        <Form.Label htmlFor="cbo-display-limit" className="col-form-label">
                          {t('MenuList.Filter.display_limit')}
                        </Form.Label>
                      </Col>
                      <Col sm={5}>
                        <Form.Select id="cbo-display-limit" className="text-center" {...register('display_limit')}>
                          {DISPLAY_LIMIT.map((item) => {
                            return (
                              <option key={item.value} value={item.value}>
                                {item.key}
                              </option>
                            );
                          })}
                        </Form.Select>
                      </Col>
                    </Row>
                  </div>
                  <div className="text-end">
                    <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                      {t('MenuList.Filter.btn_search')}
                    </button>
                  </div>
                </div>
              </Col>
            </Row>
          </Form>
        </Accordion.Body>
        <div className="accordion-body accordion-summary">
          <Row>
            <MenuClassificationPreview control={control} />
            <MenuTypePreview control={control} />
            <MenuPropertyPreview control={control} />
            <DeletedDataPreview control={control} />
            <MenuNamePreview control={control} />
            <MenuCodePreview control={control} />
            <SearchMenuConditionPreview control={control} />
            <SearchDisplayTimingPreview control={control} />

            <SearchDisplayControlGroupPreview listDisplayControlGroup={listDisplayControlGroup} control={control} />
            <SearchDisplayTimingGroupPreview listDisplayTimingGroup={listDisplayTimingGroup} control={control} />
            <SourceSearchDisplayControlGroupPreview
              listDisplayControlGroup={listDisplayControlGroup}
              control={control}
            />

            <SearchBarGuidancePreview listGuidances={listGuidances} control={control} />
            <SearchCoursePreview listCourses={listCourses} control={control} />
            <SearchOrderLimitPreview orderLimit={orderLimit} control={control} />
            <ExpiredDatePreview control={control} />
            <MenuPosPreview control={control} />
            <DisplayLimitPreview control={control} />
          </Row>
        </div>
      </Accordion.Item>
    </Accordion>
  );
}

function MenuClassificationPreview({ control }: { control: Control<FormSearchValues, any> }) {
  const { t } = useTranslation();
  const classificationOptions = useAppSelector(selectClassificationOptions);
  const menuClassification = useWatch({ control, name: 'menu_classification' });

  let text = t('MenuList.Filter.no_selection');
  if (menuClassification) {
    const classification = classificationOptions.find((opt) => opt.value === menuClassification);
    if (classification) {
      text = classification.label;
    }
  }

  return (
    <Col sm={'auto'}>
      <Form.Label className="col-form-label">{t('MenuList.Filter.menu_classification')}</Form.Label>
      <Form.Label className="col-form-label">{text}</Form.Label>
    </Col>
  );
}

function MenuTypePreview({ control }: { control: Control<FormSearchValues, any> }) {
  const { t } = useTranslation();
  const searchMenuType = Number(useWatch({ control, name: 'menu_type_conditions' }));

  let text = '-';
  const menuType = SEARCH_MENU_TYPE_CONDITIONS.find((item) => item.value === searchMenuType);
  if (menuType) {
    text = menuType.key;
  }

  return (
    <Col sm={'auto'}>
      <Form.Label className="col-form-label">{t('MenuList.Filter.search_menu_type_condition')}</Form.Label>
      <Form.Label className="col-form-label">{text}</Form.Label>
    </Col>
  );
}

function MenuPropertyPreview({ control }: { control: Control<FormSearchValues, any> }) {
  const { t } = useTranslation();
  const menuProperty = Number(useWatch({ control, name: 'menu_property' }));
  const menuProperties = MENU_PROPERTIES.find((item) => item.value === menuProperty);

  return (
    <Col sm={'auto'}>
      <Form.Label className="col-form-label">{t('MenuList.Filter.properties')}</Form.Label>
      <Form.Label className="col-form-label">{menuProperties?.label}</Form.Label>
    </Col>
  );
}

function MenuNamePreview({ control }: { control: Control<FormSearchValues, any> }) {
  const { t } = useTranslation();
  const menuName = useWatch({ control, name: 'menu_name' });

  return (
    <Col sm={'auto'}>
      <Form.Label className="col-form-label">{t('MenuList.Filter.menu_name')}</Form.Label>
      <Form.Label className="col-form-label">{menuName.length > 0 ? menuName : '-'}</Form.Label>
    </Col>
  );
}

function MenuCodePreview({ control }: { control: Control<FormSearchValues, any> }) {
  const { t } = useTranslation();
  const menuCode = useWatch({ control, name: 'menu_code' });

  return (
    <Col sm={'auto'}>
      <Form.Label className="col-form-label">{t('MenuList.Filter.menu_code')}</Form.Label>
      <Form.Label className="col-form-label">{menuCode.length > 0 ? menuCode : '-'}</Form.Label>
    </Col>
  );
}

function SearchMenuConditionPreview({ control }: { control: Control<FormSearchValues, any> }) {
  const { t } = useTranslation();
  const searchMenuCondition = Number(useWatch({ control, name: 'search_menu_condition' }));
  const menuCondition = DISPLAY_TIMING_TYPES.find((item) => item.value === searchMenuCondition);
  return (
    <Col sm={'auto'}>
      <Form.Label className="col-form-label">{t('MenuList.Filter.display_timing')}</Form.Label>
      <Form.Label className="col-form-label">{menuCondition?.label}</Form.Label>
    </Col>
  );
}

function SearchDisplayTimingPreview({ control }: { control: Control<FormSearchValues, any> }) {
  const { t } = useTranslation();
  const searchMenuCondition = Number(useWatch({ control, name: 'search_menu_condition' }));
  const searchDisplayTiming = Number(useWatch({ control, name: 'search_display_timing' }));

  if (searchMenuCondition !== 1) {
    return null;
  }

  let text = t('MenuList.Filter.no_selection');
  const displayTiming = MENU_DISPLAY_TIMING.find((item) => item.value === searchDisplayTiming);
  if (displayTiming) {
    text = displayTiming.label;
  }

  return (
    <Col sm={'auto'}>
      <Form.Label className="col-form-label">{t('MenuList.Filter.control_condition')}</Form.Label>
      <Form.Label className="col-form-label">{text}</Form.Label>
    </Col>
  );
}

function SearchDisplayControlGroupPreview({
  listDisplayControlGroup,
  control,
}: {
  listDisplayControlGroup: DisplayControlGroupType[];
  control: Control<FormSearchValues, any>;
}) {
  const { t } = useTranslation();
  const searchMenuCondition = Number(useWatch({ control, name: 'search_menu_condition' }));
  const searchDisplayTiming = Number(useWatch({ control, name: 'search_display_timing' }));
  const searchDisplayControlGroupCd = Number(useWatch({ control, name: 'search_display_control_group_cd' }));

  if (searchMenuCondition !== 1 || searchDisplayTiming !== 6) {
    return null;
  }

  let text = t('MenuList.Filter.no_selection');
  const displayTiming = listDisplayControlGroup.find((item) => item.value === searchDisplayControlGroupCd);
  if (displayTiming) {
    text = displayTiming.key;
  }

  return (
    <Col sm={'auto'}>
      <Form.Label className="col-form-label">{t('MenuList.Filter.display_control_group')}</Form.Label>
      <Form.Label className="col-form-label">{text}</Form.Label>
    </Col>
  );
}

function SearchDisplayTimingGroupPreview({
  listDisplayTimingGroup,
  control,
}: {
  listDisplayTimingGroup: DisplayTimingGroupType[];
  control: Control<FormSearchValues, any>;
}) {
  const { t } = useTranslation();
  const searchMenuCondition = Number(useWatch({ control, name: 'search_menu_condition' }));
  const searchDisplayTiming = Number(useWatch({ control, name: 'search_display_timing' }));
  const searchDisplayTiminingGroupCd = Number(useWatch({ control, name: 'search_display_timing_group_cd' }));

  if (searchMenuCondition !== 1 || searchDisplayTiming !== 7) {
    return null;
  }

  let text = t('MenuList.Filter.no_selection');
  const displayTiming = listDisplayTimingGroup.find((item) => item.value === searchDisplayTiminingGroupCd);
  if (displayTiming) {
    text = displayTiming.key;
  }

  return (
    <Col sm={'auto'}>
      <Form.Label className="col-form-label">{t('MenuList.Filter.display_timing_group')}</Form.Label>
      <Form.Label className="col-form-label">{text}</Form.Label>
    </Col>
  );
}

//
function SourceSearchDisplayControlGroupPreview({
  listDisplayControlGroup,
  control,
}: {
  listDisplayControlGroup: DisplayControlGroupType[];
  control: Control<FormSearchValues, any>;
}) {
  const { t } = useTranslation();
  const searchMenuCondition = Number(useWatch({ control, name: 'search_menu_condition' }));
  const searchSourceDisplayControlGroupCd = Number(
    useWatch({ control, name: 'search_source_display_control_group_cd' })
  );

  if (searchMenuCondition !== 2) {
    return null;
  }

  let text = t('MenuList.Filter.no_selection');
  const displayTiming = listDisplayControlGroup.find((item) => item.value === searchSourceDisplayControlGroupCd);
  if (displayTiming) {
    text = displayTiming.key;
  }

  return (
    <Col sm={'auto'}>
      <Form.Label className="col-form-label">{t('MenuList.Filter.display_control_group')}</Form.Label>
      <Form.Label className="col-form-label">{text}</Form.Label>
    </Col>
  );
}

function DeletedDataPreview({ control }: { control: Control<FormSearchValues, any> }) {
  const { t } = useTranslation();
  const deletedData = Number(useWatch({ control, name: 'deleted_data' }));

  let text = '-';
  const selectedOption = MENU_DELETE_OPTIONS.find((item) => item.value === deletedData);
  if (selectedOption) {
    text = selectedOption.key;
  }

  return (
    <Col sm={'auto'}>
      <Form.Label className="col-form-label">{t('MenuList.Filter.deleted_data')}</Form.Label>
      <Form.Label className="col-form-label">{text}</Form.Label>
    </Col>
  );
}

function SearchBarGuidancePreview({
  listGuidances,
  control,
}: {
  listGuidances: BarInfo[];
  control: Control<FormSearchValues, any>;
}) {
  const { t } = useTranslation();
  const searchBarGuidance = Number(useWatch({ control, name: 'search_bar_guidance' }));

  let text = t('MenuList.Filter.no_selection');
  const selectedOption = listGuidances.find((item) => item.id === searchBarGuidance);
  if (selectedOption) {
    text = selectedOption.name;
  }

  return (
    <Col sm={'auto'}>
      <Form.Label className="col-form-label">{t('MenuList.Filter.bar_guidance_menu')}</Form.Label>
      <Form.Label className="col-form-label">{text}</Form.Label>
    </Col>
  );
}

function SearchCoursePreview({
  listCourses,
  control,
}: {
  listCourses: Course[];
  control: Control<FormSearchValues, any>;
}) {
  const { t } = useTranslation();
  const searchCourse = Number(useWatch({ control, name: 'search_course' }));

  let text = t('MenuList.Filter.no_selection');
  const selectedOption = listCourses.find((item) => item.id === searchCourse);
  if (selectedOption) {
    text = selectedOption.name;
  }

  return (
    <Col sm={'auto'}>
      <Form.Label className="col-form-label">{t('MenuList.Filter.course_target_menu')}</Form.Label>
      <Form.Label className="col-form-label">{text}</Form.Label>
    </Col>
  );
}

function SearchOrderLimitPreview({
  orderLimit,
  control,
}: {
  orderLimit: { key: number; value: string }[];
  control: Control<FormSearchValues, any>;
}) {
  const { t } = useTranslation();
  const searchOrderLimit = Number(useWatch({ control, name: 'search_order_limit' }));

  let text = t('MenuList.Filter.no_selection');
  const selectedOption = orderLimit.find((item) => item.key === searchOrderLimit);
  if (selectedOption) {
    text = selectedOption.value;
  }

  return (
    <Col sm={'auto'}>
      <Form.Label className="col-form-label">{t('MenuList.Filter.course_set_condition_menu')}</Form.Label>
      <Form.Label className="col-form-label">{text}</Form.Label>
    </Col>
  );
}

function ExpiredDatePreview({ control }: { control: Control<FormSearchValues, any> }) {
  const { t } = useTranslation();
  const expiredDate = useWatch({ control, name: 'expired_date' });

  return (
    <Col sm={'auto'}>
      <Form.Label className="col-form-label">{t('MenuList.Filter.date_of_expires')}：</Form.Label>
      <Form.Label className="col-form-label">{moment(expiredDate).format(DATE_FORMAT_YYYY_MM_DD)}</Form.Label>
    </Col>
  );
}

function MenuPosPreview({ control }: { control: Control<FormSearchValues, any> }) {
  const { t } = useTranslation();
  const menuPos = useWatch({ control, name: 'menu_pos' });

  return (
    <Col sm={'auto'}>
      <Form.Label className="col-form-label"> {t('MenuList.Filter.pos')}：</Form.Label>
      <Form.Label className="col-form-label">{menuPos.length > 0 ? menuPos : '-'}</Form.Label>
    </Col>
  );
}

function DisplayLimitPreview({ control }: { control: Control<FormSearchValues, any> }) {
  const { t } = useTranslation();
  const displayLimit = Number(useWatch({ control, name: 'display_limit' }));

  // DISPLAY_LIMIT
  let text = '-';
  const selectedOption = DISPLAY_LIMIT.find((item) => item.value === displayLimit);
  if (selectedOption) {
    text = selectedOption.key;
  }

  return (
    <Col sm={'auto'}>
      <Form.Label className="col-form-label">{t('MenuList.Filter.display_limit')}</Form.Label>
      <Form.Label className="col-form-label">{text}</Form.Label>
    </Col>
  );
}

const CustomInputDatePicker = forwardRef((props: any, ref: any) => {
  // You can style this further
  return (
    <>
      <div style={{ position: 'relative' }}>
        <FaCalendarAlt style={{ position: 'absolute', top: '8px', left: '10px' }} onClick={props.onClick} />
        <input
          className="form-control text-center"
          onClick={props.onClick}
          style={{ paddingLeft: '32px' }}
          value={props.value}
          onChange={props.onChange}
          type="text"
          readOnly={props.readonly}
        />
      </div>
    </>
  );
});

export default MenuListFilter;
